import apiClient from "./apiClient";

const invalidateProductsCache = () => {
  localStorage.removeItem('products');
  localStorage.removeItem('productsTimestamp');
};

const refreshProductsCache = async () => {
  try {
    const response = await apiClient.get("/products/");
    localStorage.setItem('products', JSON.stringify(response.data));
    localStorage.setItem('productsTimestamp', Date.now().toString());
    return response.data;
  } catch (error) {
    console.error('Error refreshing products cache:', error);
    throw error;
  }
};

const getAuthToken = () => localStorage.getItem("accessToken");

export const getProducts = async (params = {}, forceRefresh = false) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    if (params.search) queryParams.append('search', params.search);
    if (params.category) queryParams.append('category_name', params.category);
    if (params.no_prices) queryParams.append('no_prices', params.no_prices);
    
    if (forceRefresh) {
      queryParams.append('_t', Date.now());
    }

    console.log('Request URL:', `/products/?${queryParams.toString()}`);
    
    const response = await apiClient.get(`/products/?${queryParams.toString()}`, {
      headers: forceRefresh ? {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      } : {},
      timeout: 40000
    });
    
    console.log('Products API response:', response.data);
    
    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [], count: 0 };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    return { results: [], count: 0 };
  }
};

export const uploadImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('upload_preset', 'productos');
    formData.append('cloud_name', 'dkz9zwyak');
    formData.append('file', file);

    const response = await fetch('https://api.cloudinary.com/v1_1/dkz9zwyak/image/upload', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error('Error uploading to Cloudinary:', error);
    throw new Error('Error al subir la imagen: ' + (error.message || 'Error desconocido'));
  }
};

export const getProductsOnSale = async () => {
  try {
    const response = await apiClient.get('/products/on-sale/', {
      timeout: 15000
    });

    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [] };
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching products on sale:', error);
    return { results: [] };
  }
};

export const getProduct = async (id) => {
  try {
    const response = await apiClient.get(`/products/${id}/`);
    
    if (!response.data) {
      throw new Error('No se encontró el producto');
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching product:', error.response || error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.error || 
      'Error al obtener el producto'
    );
  }
};

export const createProduct = async (productData) => {
  try {
    console.log('Creando producto:', productData);
    const response = await apiClient.post("/products/", productData, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    await refreshProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al crear el producto');
  }
};

export const updateProduct = async (productId, productData) => {
  try {
    const dataToSend = {
      ...productData,
      name: productData.name?.trim(),
      brand: (productData.brand || '').trim(),
      description: (productData.description || '').trim(),
      category: productData.category || '',
      price: Number(productData.price || 0),
      stock: Number(productData.stock || 0),
      discount: Number(productData.discount || 0),
      volume: (productData.volume || '').trim(),
      dimensions: (productData.dimensions || '').trim(),
      image: productData.image || null,
      image_2: productData.image_2 || null,
      features: Array.isArray(productData.features) ? productData.features : [],
      is_available: Boolean(productData.is_available)
    };

    console.log('Sending update data:', dataToSend);

    const response = await apiClient.put(`/products/${productId}/`, dataToSend, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    await refreshProductsCache();
    return response.data;
  } catch (error) {
    console.error('Error updating product:', error.response || error);
    throw new Error(
      error.response?.data?.error || 
      error.response?.data?.message || 
      'Error al actualizar el producto'
    );
  }
};

export const deleteProduct = async (productId) => {
  try {
    const token = getAuthToken();
    const response = await apiClient.delete(`/products/${productId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    
    // Invalidate cache and force refresh
    invalidateProductsCache();
    await refreshProductsCache();
    
    return {
      success: true,
      message: response.data?.message || 'Producto eliminado correctamente'
    };
  } catch (error) {
    console.error('Error deleting product:', error.response || error);
    throw new Error(
      error.response?.data?.error || 
      error.response?.data?.message || 
      'Error al eliminar el producto'
    );
  }
};

export const getCategories = async () => {
  try {
    const token = getAuthToken();
    const response = await apiClient.get('/products/categories/', {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw new Error('Error al cargar las categorías');
  }
};

export const createCategory = async (categoryData) => {
  try {
    const token = getAuthToken();
    const response = await apiClient.post('/categories/', categoryData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating category:', error);
    throw new Error(error.response?.data?.message || error.response?.data?.error || 'Error al crear la categoría');
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    const token = getAuthToken();
    const response = await apiClient.delete(`/categories/${categoryId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw new Error(
      error.response?.data?.error || 
      error.response?.data?.message || 
      'Error al eliminar la categoría'
    );
  }
};

export const getRelatedProducts = async (productId) => {
  try {
    const response = await apiClient.get(`/products/${productId}/related/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching related products:', error);
    return { results: [] };
  }
};

export const updatePrices = async (params) => {
  const response = await apiClient.put('/products/bulk-update-prices/', params);
  return response.data;
};

export const testApiConnection = async () => {
  try {
    console.log('Testing API connection...');
    const response = await apiClient.get('/products/', { 
      params: { page: 1, page_size: 1 },
      timeout: 10000
    });
    
    console.log('API test successful:', response.status);
    return {
      success: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('API test failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getProductsWithoutPrice = async () => {
  try {
    const response = await apiClient.get('/products/without-price')
    return response.data
  } catch (error) {
    throw error.response?.data || { error: 'Error al obtener productos' }
  }
};

export const getBrands = async () => {
  try {
    const response = await apiClient.get('/products/brands/');
    return response.data;
  } catch (error) {
    console.error('Error fetching brands:', error);
    throw new Error('Error al cargar las marcas');
  }
};
